import React, { useState } from 'react';
import {
  Alert,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { Braces, ChevronDown, Lock, Plus, Trash2 } from 'lucide-react';
import PathField from './PathField';
import styles from './PreprocessingSection.module.scss';
import {
  DERIVED_CASES,
  DERIVED_FALLBACKS,
  DERIVED_MODES,
  DERIVED_MODE_HINT,
  PREPROCESSING_LOCK_REASON,
  PREPROCESSING_NAMESPACE,
  emptyCandidate,
  emptyDecodeEntry,
  emptyDerivedField,
  emptyPreprocessing,
  hasPreprocessing,
  joinFieldWarnings,
  preprocessingNamespace,
} from './kgUtils';

/**
 * `mapping_config.preprocessing` — the input-normalization editor.
 *
 * This exists because customer exports are not flat, and the two shapes that
 * break a naive mapping are both common:
 *
 *   • the useful fields are inside a JSON STRING column (every event pipeline
 *     that writes a `payload` column does this), so nothing below it is
 *     addressable until it is decoded;
 *   • the same logical field lives under a different name per source, so one
 *     path covers a third of the file and looks like 33% coverage rather than
 *     like three columns that should have been one.
 *
 * Both are fixed server-side, on the way in. Whatever is derived lands under a
 * namespace (`normalized` by default) and is then addressable everywhere else in
 * the config — the originals are never overwritten.
 *
 * The section locks once records exist: this decides record IDENTITY and what
 * text gets embedded, so changing it would silently re-key data already loaded.
 * The API rejects it; locking is kinder than letting someone redo the work first.
 *
 * `when` / `select` / `date` on a candidate are passed through as raw JSON. Their
 * shapes belong to the server and are richer than a two-field form can express,
 * so this edits them honestly rather than flattening them into something that
 * would round-trip badly.
 */
const PreprocessingSection = ({
  config,
  patch,
  fields,
  // Forwarded to every PathField below: whether `fields` was inspected from a
  // real sample, or is only what the saved config already references.
  hasSample = fields?.length > 0,
  locked,
  derivedFieldNames = [],
}) => {
  const [open, setOpen] = useState(() => hasPreprocessing(config));
  const [expanded, setExpanded] = useState({});
  const [jsonErrors, setJsonErrors] = useState({});

  const pre = config.preprocessing || null;
  const namespace = preprocessingNamespace(config);
  const decode = Array.isArray(pre?.decode) ? pre.decode : [];
  const derived = Array.isArray(pre?.fields) ? pre.fields : [];
  const active = hasPreprocessing(config);

  /** Every mutation goes through here so the block is created on first use. */
  const patchPre = (updater) =>
    patch((next) => {
      if (!next.preprocessing) next.preprocessing = emptyPreprocessing();
      if (!Array.isArray(next.preprocessing.decode)) next.preprocessing.decode = [];
      if (!Array.isArray(next.preprocessing.fields)) next.preprocessing.fields = [];
      updater(next.preprocessing);
    });

  const setCandidateJson = (fieldIndex, candidateIndex, key, text) => {
    const id = `${fieldIndex}-${candidateIndex}-${key}`;
    const raw = String(text || '').trim();
    if (!raw) {
      setJsonErrors((prev) => ({ ...prev, [id]: null }));
      patchPre((block) => {
        delete block.fields[fieldIndex].candidates[candidateIndex][key];
      });
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setJsonErrors((prev) => ({ ...prev, [id]: null }));
      patchPre((block) => {
        block.fields[fieldIndex].candidates[candidateIndex][key] = parsed;
      });
    } catch (e) {
      // Kept as an inline error rather than discarded: half-typed JSON is the
      // normal state of a field like this.
      setJsonErrors((prev) => ({ ...prev, [id]: e.message }));
    }
  };

  return (
    <section className={styles.section}>
      <button type="button" className={styles.collapseToggle} onClick={() => setOpen((v) => !v)}>
        <ChevronDown
          size={14}
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        <Braces size={14} />
        Input normalization
        {active && (
          <span className={styles.activeTag}>
            {decode.length + derived.length} rule
            {decode.length + derived.length === 1 ? '' : 's'}
          </span>
        )}
        {locked && (
          <Tooltip title={PREPROCESSING_LOCK_REASON}>
            <span className={styles.lockBadge}>
              <Lock size={11} /> locked
            </span>
          </Tooltip>
        )}
      </button>

      {open && (
        <div className={styles.body}>
          {locked ? (
            <Alert severity="info" className={styles.inlineAlert}>
              {PREPROCESSING_LOCK_REASON}
            </Alert>
          ) : (
            <p className={styles.help}>
              For records that aren’t flat. Unpack a JSON string column, or build one field out of
              several that mean the same thing. Results land under{' '}
              <code>{namespace}</code> and can then be used as the row identity, the title, a
              date, a facet or text. Your original fields are never overwritten.
            </p>
          )}

          {/* ── Namespace ── */}
          {active && (
            <div className={styles.namespaceRow}>
              <TextField
                size="small"
                label="Derived values live under"
                value={pre?.namespace || PREPROCESSING_NAMESPACE}
                onChange={(e) =>
                  patchPre((block) => {
                    block.namespace = e.target.value;
                  })
                }
                disabled={locked}
                helperText={`Referenced as ${namespace}.field_name elsewhere in the mapping.`}
                InputProps={{ className: styles.monoInput }}
              />
            </div>
          )}

          {/* ── decode[] ── */}
          <div className={styles.block}>
            <div className={styles.blockHead}>
              <strong>Decode JSON text columns</strong>
              <span>
                A column whose value is a JSON string rather than an object. Nothing inside it can
                be addressed until it is parsed.
              </span>
            </div>
            {decode.map((entry, index) => (
              <div key={index} className={styles.decodeRow}>
                <PathField
                  value={entry.path || ''}
                  onChange={(v) =>
                    patchPre((block) => {
                      block.decode[index].path = v;
                    })
                  }
                  fields={fields}
                  hasSample={hasSample}
                  label="Column holding JSON"
                  disabled={locked}
                />
                <span className={styles.arrow}>→</span>
                <TextField
                  size="small"
                  label="Call the result"
                  value={entry.target || ''}
                  onChange={(e) =>
                    patchPre((block) => {
                      block.decode[index].target = e.target.value;
                    })
                  }
                  disabled={locked}
                  helperText={`Addressable as ${namespace}.${entry.target || 'name'}…`}
                  InputProps={{ className: styles.monoInput }}
                />
                {!locked && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      patchPre((block) => {
                        block.decode.splice(index, 1);
                      })
                    }
                  >
                    <Trash2 size={14} />
                  </IconButton>
                )}
              </div>
            ))}
            {!locked && (
              <Button
                size="small"
                variant="text"
                startIcon={<Plus size={14} />}
                onClick={() =>
                  patchPre((block) => {
                    block.decode.push(emptyDecodeEntry());
                  })
                }
              >
                Decode a column
              </Button>
            )}
          </div>

          {/* ── fields[] ── */}
          <div className={styles.block}>
            <div className={styles.blockHead}>
              <strong>Derived fields</strong>
              <span>
                One field built from several candidate columns, tried in order. This is what a
                mixed-source export needs: the id is <code>order_id</code> on one source and{' '}
                <code>id</code> on another, and both should be the same field.
              </span>
            </div>

            {derived.map((field, index) => {
              const candidates = Array.isArray(field.candidates) ? field.candidates : [];
              const isOpen = expanded[index] !== false;
              return (
                <div key={index} className={styles.derivedCard}>
                  <div className={styles.derivedHead}>
                    <button
                      type="button"
                      className={styles.derivedToggle}
                      onClick={() =>
                        setExpanded((prev) => ({ ...prev, [index]: prev[index] === false }))
                      }
                      aria-expanded={isOpen}
                    >
                      <ChevronDown
                        size={14}
                        style={{ transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}
                      />
                      <code className={styles.derivedName}>
                        {namespace}.{field.name || 'unnamed'}
                      </code>
                    </button>
                    <span className={styles.derivedMeta}>
                      {candidates.length} candidate{candidates.length === 1 ? '' : 's'}
                    </span>
                    {!locked && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          patchPre((block) => {
                            block.fields.splice(index, 1);
                          })
                        }
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    )}
                  </div>

                  {isOpen && (
                    <>
                      <div className={styles.derivedRow}>
                        <TextField
                          size="small"
                          label="Field name"
                          value={field.name || ''}
                          onChange={(e) =>
                            patchPre((block) => {
                              block.fields[index].name = e.target.value;
                            })
                          }
                          disabled={locked}
                          helperText="Lowercase letters, digits and underscores."
                          InputProps={{ className: styles.monoInput }}
                        />
                        <TextField
                          select
                          size="small"
                          label="When several candidates match"
                          value={field.mode || 'first'}
                          onChange={(e) =>
                            patchPre((block) => {
                              block.fields[index].mode = e.target.value;
                            })
                          }
                          disabled={locked}
                          className={styles.wideSelect}
                        >
                          {DERIVED_MODES.map((mode) => (
                            <MenuItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        {field.mode === 'join' && (
                          <TextField
                            size="small"
                            label="Joined with"
                            value={field.separator ?? ' '}
                            onChange={(e) =>
                              patchPre((block) => {
                                block.fields[index].separator = e.target.value;
                              })
                            }
                            disabled={locked}
                            className={styles.narrowField}
                          />
                        )}
                      </div>

                      {/* The one-line rule, right under the choice it governs. */}
                      <span className={styles.modeHint}>{DERIVED_MODE_HINT}</span>
                      {joinFieldWarnings(field, config).map((warning) => (
                        <span key={warning} className={styles.modeWarn}>
                          {warning}
                        </span>
                      ))}

                      <div className={styles.derivedRow}>
                        <TextField
                          select
                          size="small"
                          label="Case"
                          value={field.case || ''}
                          onChange={(e) =>
                            patchPre((block) => {
                              block.fields[index].case = e.target.value;
                            })
                          }
                          disabled={locked}
                          className={styles.narrowSelect}
                        >
                          {DERIVED_CASES.map((option) => (
                            <MenuItem key={option.value || 'none'} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        {/* The reason `content_hash` exists: an export where some
                            rows genuinely carry no id. A stable synthetic key
                            keeps them rather than dropping them on the floor. */}
                        <Tooltip title="What to use when no candidate has a value. A content hash gives the record a stable identity of its own rather than dropping it.">
                          <TextField
                            select
                            size="small"
                            label="If nothing matched"
                            value={field.fallback || ''}
                            onChange={(e) =>
                              patchPre((block) => {
                                block.fields[index].fallback = e.target.value;
                              })
                            }
                            disabled={locked}
                            className={styles.wideSelect}
                          >
                            {DERIVED_FALLBACKS.map((option) => (
                              <MenuItem key={option.value || 'none'} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Tooltip>
                        <TextField
                          size="small"
                          type="number"
                          label="Max characters"
                          value={field.max_chars ?? ''}
                          onChange={(e) =>
                            patchPre((block) => {
                              block.fields[index].max_chars = e.target.value
                                ? Number(e.target.value)
                                : null;
                            })
                          }
                          disabled={locked}
                          className={styles.narrowField}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={Boolean(field.required)}
                              onChange={(e) =>
                                patchPre((block) => {
                                  block.fields[index].required = e.target.checked;
                                })
                              }
                              disabled={locked}
                            />
                          }
                          label={
                            <Tooltip title="Reject any record where this field ends up empty, instead of loading it without one.">
                              <span className={styles.switchLabel}>Required</span>
                            </Tooltip>
                          }
                        />
                      </div>

                      {/* ── candidates ── ordered, and the order is the logic ── */}
                      <div className={styles.candidates}>
                        <span className={styles.candidatesLabel}>
                          Read from, in order:
                        </span>
                        {candidates.map((candidate, c) => (
                          <div key={c} className={styles.candidateRow}>
                            <span className={styles.candidateIndex}>{c + 1}</span>
                            <PathField
                              value={candidate.path || ''}
                              onChange={(v) =>
                                patchPre((block) => {
                                  block.fields[index].candidates[c].path = v;
                                })
                              }
                              fields={fields}
                              hasSample={hasSample}
                              label={c === 0 ? 'Column' : 'else'}
                              disabled={locked}
                            />
                            {/* Server-shaped options. Rendered as JSON on purpose
                                — inventing a form for them would round-trip a
                                richer config badly. */}
                            {['when', 'select', 'date'].map((key) => {
                              const id = `${index}-${c}-${key}`;
                              const stored = candidate[key];
                              const hasValue = stored !== undefined && stored !== null;
                              if (!hasValue && locked) return null;
                              return (
                                <TextField
                                  key={key}
                                  size="small"
                                  label={key}
                                  defaultValue={hasValue ? JSON.stringify(stored) : ''}
                                  onBlur={(e) => setCandidateJson(index, c, key, e.target.value)}
                                  disabled={locked}
                                  error={Boolean(jsonErrors[id])}
                                  helperText={jsonErrors[id] || ''}
                                  placeholder="optional JSON"
                                  className={styles.jsonField}
                                  InputProps={{ className: styles.monoInput }}
                                />
                              );
                            })}
                            {!locked && candidates.length > 1 && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  patchPre((block) => {
                                    block.fields[index].candidates.splice(c, 1);
                                  })
                                }
                              >
                                <Trash2 size={13} />
                              </IconButton>
                            )}
                          </div>
                        ))}
                        {!locked && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<Plus size={13} />}
                            onClick={() =>
                              patchPre((block) => {
                                block.fields[index].candidates.push(emptyCandidate());
                              })
                            }
                          >
                            Add a fallback column
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}

            {!locked && (
              <Button
                size="small"
                variant="text"
                startIcon={<Plus size={14} />}
                onClick={() =>
                  patchPre((block) => {
                    block.fields.push(emptyDerivedField());
                  })
                }
              >
                Derive a field
              </Button>
            )}
          </div>

          {/* Nothing here is any use unless something else points at it. */}
          {active && derivedFieldNames.length > 0 && (
            <p className={styles.help}>
              Use these anywhere a column is asked for:{' '}
              {derivedFieldNames.map((name) => (
                <code key={name} className={styles.pathChip}>
                  {namespace}.{name}
                </code>
              ))}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default PreprocessingSection;
